import Route from 'cio/routes/base';
import VWBDemoController from './controller';
import Transition from '@ember/routing/-private/transition';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import AuthService from 'cio/services/auth';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import CampaignModel, { Edge } from 'cio/models/campaign';
import ActionModel from 'cio/models/action';
import { getOwner } from '@ember/application';
import EnvironmentService from 'cio/services/environment';
import { queryEncode } from 'cio/helpers/query';
import config from 'cio/config/environment';
import FeaturesService from 'ember-feature-flags/services/features';
import { DEMO_SEGMENT_CONFIG } from 'cio/utils/visual-workflow-builder/demo/segments';
import { getEnvironmentConfig } from 'cio/utils/visual-workflow-builder/demo/environment';
import { action } from '@ember/object';
import WindowMessengerService from 'cio/services/window-messenger';
import { FlyMouseEvent } from '@customerio/fly-components/types/fly-components/events';
import { resolve } from 'rsvp';
import DemoAnalyticsService from 'cio/utils/visual-workflow-builder/demo/services/analytics';
import DemoApiService from 'cio/utils/visual-workflow-builder/demo/services/api';
import DemoAuthService from 'cio/utils/visual-workflow-builder/demo/services/auth';
import DemoFiltersService from 'cio/utils/visual-workflow-builder/demo/services/filters';
import DemoCampaignNoteAdapter from 'cio/utils/visual-workflow-builder/demo/adapters/campaign-note';
import DemoCustomerAdapter from 'cio/utils/visual-workflow-builder/demo/adapters/customer';
import DemoSubjectAdapter from 'cio/utils/visual-workflow-builder/demo/adapters/subject';
import { getConfig } from 'cio/utils/visual-workflow-builder/demo/versions';
import { USER_ROLE_AUTHOR } from 'cio/utils/user-roles';
import { singularize, pluralize } from 'ember-inflector';
import { underscore } from '@ember/string';

function refreshFiltersService(model: CampaignModel | ActionModel) {
  // Makes sure that all actions we add or delete update the filters service, so
  // that the latest list of actions is always available in "message" conditions
  getOwner(model).lookup('service:filters').process.perform();
}

function prepareFakeActionPayload(serializedAction: any) {
  let dataCopy = { ...serializedAction };

  dataCopy.action_state = 'running';

  if (dataCopy.type === 'webhook_action') {
    dataCopy.request_method = 'POST';
  }

  if (dataCopy.type === 'multi_split_branch_action') {
    dataCopy.multi_conditions = [queryEncode([]), queryEncode([])];
  }

  return dataCopy;
}

async function fakeCampaignAddActions(this: CampaignModel, { actions, edges }: { actions: any; edges: Edge[] }) {
  let newActionIdsMap: Record<string, string> = {};

  // It's possible to add multiple actions at once, for example when copying
  // many actions.
  // Each action will have a temporary ID assigned, starting from -1 and
  // decrementing into the negatives. For each of those actions we generate a
  // "real" Ember Data ID and push it to the store. We then update those
  // negative IDs in the new edges as well for the campaign.
  actions.forEach((a: any) => {
    if (a.parent_action_id != null) {
      // Ignore split child actions - they will be created when the split itself
      // is created
      return;
    }
    let nextId = String(getNextStoreId(this.store, 'action'));
    newActionIdsMap[a.id] = nextId;

    let actionPayload = prepareFakeActionPayload({
      ...a,
      id: nextId,
      campaign_id: this.id,
    });

    this.store.pushPayload({ actions: [actionPayload] });

    if (actionPayload.type === 'split_randomized_action') {
      // If a split action is copied, we need to copy the child actions as well,
      // and link the split to the child actions.
      // Child actions when copying have IDs of 0, but no edges coming in/out,
      // so we don't need to worry about those.
      let childActions = actions
        .filter((maybeChildAction: any) => maybeChildAction.parent_action_id === a.id)
        .map((serializedChildAction: any) => {
          let childId = String(getNextStoreId(this.store, 'action'));
          let actionPayload = prepareFakeActionPayload({
            ...serializedChildAction,
            id: childId,
            campaign_id: this.id,
            parent_action_id: nextId,
          });
          this.store.pushPayload({ actions: [actionPayload] });
          return this.store.peekRecord('action', childId);
        });

      this.store.peekRecord('action', nextId)?.set('actions', childActions);
    }
  });
  let newEdges = edges.map((e) => ({
    ...e,
    from: newActionIdsMap[e.from] ?? e.from,
    to: newActionIdsMap[e.to] ?? e.to,
  }));

  this.store.peekAll('action').forEach((a) => stubActionModelMethods(a));
  this.setProperties({
    workflowActionIds: [...this.workflowActionIds, ...Object.values(newActionIdsMap)],
    edges: newEdges,
    lastParseTreeTimestamp: 0,
    lastParseTreeUpdatedAt: 0,
  });

  refreshFiltersService(this);

  return resolve();
}

async function fakeCampaignMoveAction(this: CampaignModel, { edges }: { edges: Edge[] }) {
  this.setProperties({
    edges,
    lastParseTreeTimestamp: 0,
    lastParseTreeUpdatedAt: 0,
  });

  return resolve();
}

async function fakeCampaignDeleteActions(
  this: CampaignModel,
  { actionIds, edges }: { actionIds: string[]; edges: Edge[] },
) {
  let newWorkflowActionIds = this.workflowActionIds.filter((id) => !actionIds.includes(id));

  this.setProperties({
    workflowActionIds: newWorkflowActionIds,
    edges,
    lastParseTreeTimestamp: 0,
    lastParseTreeUpdatedAt: 0,
  });

  // No need to unload the actual action records - the delete methods in the
  // campaign and VWB do the unloading already

  refreshFiltersService(this);

  return resolve();
}

async function fakeCampaignSaveAction(this: CampaignModel, { edges }: { edges: Edge[] }) {
  this.set('edges', this.expandConsolidatedEdges(edges));

  refreshFiltersService(this);

  return resolve();
}

async function fakeActionSave(this: ActionModel) {
  refreshFiltersService(this);
  return resolve();
}

async function fakeActionCreateSplit(this: ActionModel) {
  let campaign = this.belongsTo('campaign').value() as CampaignModel;

  // When creating a split, the backend makes the original action into a split
  // type, while creating two new actions as clones of the original one. We need
  // to perform the same operation here to make it possible to create splits in
  // the VWB demo.
  let originalId = getNextStoreId(this.store, 'action');
  this.store.pushPayload({
    action: {
      ...this.serialize(),
      id: originalId,
    },
  });
  let variationId = getNextStoreId(this.store, 'action');
  this.store.pushPayload({
    action: {
      ...this.serialize(),
      id: variationId,
    },
  });

  let original = this.store.peekRecord('action', originalId);
  let variation = this.store.peekRecord('action', variationId);

  if (original == null || variation == null) {
    return;
  }

  this.setProperties({
    type: 'split_randomized_action',
    actions: [original, variation],
    allocations: [100, 0],
    splitState: 'inactive',
    started: 0,
  });

  // Forces VWB to rerender items (the main action's ID stays the same, but is
  // now a larger split action, so the layout needs to rerender)
  campaign.workflowActionIds = [...campaign.workflowActionIds];

  refreshFiltersService(this);

  return resolve({
    meta: {
      action_id: originalId,
    },
  });
}

async function fakeActionSelectSplitWinner(this: ActionModel, data: { campaignId: string; winnerId: string }) {
  let campaign = this.store.peekRecord('campaign', data.campaignId);

  if (campaign == null) {
    return;
  }

  let winnerAction = this.store.peekRecord('action', data.winnerId);

  if (winnerAction == null) {
    return;
  }

  let childActionIds = this.hasMany('actions').ids();

  this.setProperties({
    type: winnerAction.type,
    name: winnerAction.name,
    tracked: winnerAction.tracked,
    sendToUnsubscribed: winnerAction.sendToUnsubscribed,
    useMessageLimits: winnerAction.useMessageLimits,
    canConvert: winnerAction.canConvert,
    sendingState: winnerAction.sendingState,
    sendToPlatform: winnerAction.sendToPlatform,
    actions: [],
    allocations: [],
    splitState: 'inactive',
    started: 0,
  });

  // Forces VWB to rerender items (the main action's ID stays the same, but is
  // now no longer a split, so the layout needs to rerender)
  campaign.workflowActionIds = [...campaign.workflowActionIds];

  childActionIds.forEach((id) => this.store.peekRecord('action', id)?.unloadRecord());
  refreshFiltersService(this);

  return resolve({});
}

function stubActionModelMethods(action: ActionModel) {
  action.save = fakeActionSave.bind(action);
  action.createSplit = fakeActionCreateSplit.bind(action);
  action.selectSplitWinner = fakeActionSelectSplitWinner.bind(action);
}

let idCache: Record<string, number> = {};

function getNextStoreId(store: DS.Store, modelType: string) {
  // Use a cache to avoid reusing IDs that were used previously - for example
  // when an action is deleted. If no ID exists in cache yet, use existing
  // records to generate a new ID
  if (idCache[modelType] != null) {
    idCache[modelType] += 1;
    return String(idCache[modelType]);
  }

  let allActions = store
    .peekAll(modelType)
    .toArray()
    .sort((a, b) => {
      return parseInt(a.id) - parseInt(b.id);
    });

  if ((allActions ?? []).length === 0) {
    idCache[modelType] = 1;
  } else {
    let lastId = allActions[allActions.length - 1].id;
    idCache[modelType] = lastId + 1;
  }

  return String(idCache[modelType]);
}

type Class = { new (...args: any[]): any };

const CUSTOM_SERVICE_REPLACEMENTS: Record<`${string}:${string}`, Class> = {
  'service:api': DemoApiService,
  'service:analytics': DemoAnalyticsService,
  'service:auth': DemoAuthService,
  'service:filters': DemoFiltersService,
};

const CUSTOM_ADAPTER_REPLACEMENTS: Record<`${string}:${string}`, Class> = {
  'adapter:campaign-note': DemoCampaignNoteAdapter,
  'adapter:customer': DemoCustomerAdapter,
  'adapter:subject': DemoSubjectAdapter,
};

export default class VWBDemoRoute extends Route {
  @service account!: AccountService;
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;
  @service store!: DS.Store;
  @service windowMessenger!: WindowMessengerService;

  declare controller: VWBDemoController;

  setupController(controller: VWBDemoController, model: null, transition: Transition) {
    super.setupController(controller, model, transition);

    let user = this.createMockUser();
    this.auth.set('user', user);

    let account = this.createMockAccount();
    this.account.set('current', account);

    let environment = this.createMockEnvironment();
    this.environment.set('current', environment);

    this.createMockSegments();

    let config = getConfig(controller.version);
    let campaign: CampaignModel;
    try {
      campaign = this.createMockCampaignFromConfig(config.config);
    } catch {
      // Fall back to default config if versioned config is broken
      config = getConfig('default');
      campaign = this.createMockCampaignFromConfig(config.config);
    }
    controller.model = campaign;
    controller.campaign = campaign;
    controller.addPanelItemConfigs = config.addPanelItemConfigs;

    controller.waitForLoadToFinish.perform();
  }

  private createMockUser() {
    let user = this.store.createRecord('user', {
      id: getNextStoreId(this.store, 'user'),
      userFlags: {},
      role: USER_ROLE_AUTHOR,
    });
    return user;
  }

  private createMockAccount() {
    let account = this.store.createRecord('account', {
      id: getNextStoreId(this.store, 'account'),
      currentUser: this.auth.user,
    });
    return account;
  }

  private createMockEnvironment() {
    let config = getEnvironmentConfig(this.controller.version);

    let environment = this.store.createRecord('environment', {
      ...config,
      id: getNextStoreId(this.store, 'environment'),
    });

    environment.reload = () => resolve();

    return environment;
  }

  private createMockCampaignFromConfig(config: Record<string, any>) {
    if (config.campaign == null || config.actions == null) {
      throw new Error('Invalid config: campaign or actions not defined');
    }

    let { campaign: campaignConfig, actions: actionsConfig, ...remainingConfig } = config;

    this.store.pushPayload({ campaign: campaignConfig, actions: actionsConfig });

    let campaign = this.store.peekRecord('campaign', config.campaign.id);

    if (campaign == null) {
      throw new Error('Campaign not found');
    }

    campaign.addActions = fakeCampaignAddActions.bind(campaign);
    campaign.moveAction = fakeCampaignMoveAction.bind(campaign);
    campaign.deleteActions = fakeCampaignDeleteActions.bind(campaign);
    campaign.saveAction = fakeCampaignSaveAction.bind(campaign);

    this.store.peekAll('action').forEach((actionModel) => {
      stubActionModelMethods(actionModel);
    });

    // Push any remaining items to the store, such as segments or sticky notes
    this.store.pushPayload(remainingConfig);

    campaign.parseTree();

    return campaign;
  }

  private createMockAction(actionData: any) {
    let action = this.store.createRecord('action', actionData);
    stubActionModelMethods(action);
    return action;
  }

  private createMockSegments() {
    DEMO_SEGMENT_CONFIG.forEach((segmentData) => {
      this.store.createRecord('segment', {
        ...segmentData,
        id: getNextStoreId(this.store, 'segment'),
      });
    });
  }

  activate() {
    this.overrideServices();
    this.overrideAdapters();

    this.attachGlobalClickListener();
  }

  deactivate() {
    this.detachGlobalClickListener();

    idCache = {};
  }

  private overrideServices() {
    Object.entries(CUSTOM_SERVICE_REPLACEMENTS).forEach(([name, klass]: [`${string}:${string}`, Class]) => {
      this.replaceRegistration(name, klass);
      this.replaceServiceInstance(name);
    });
  }

  private replaceServiceInstance(fullName: `${string}:${string}`) {
    let appInstance = getOwner(this);
    let serviceInstance = appInstance.lookup(fullName);

    let [, serviceName] = fullName.split(':');

    // After re-registering services, we need to update the service instances on
    // all current routes and controllers - otherwise their injections will
    // still point to the old instances.
    let containersToReplace: `${string}:${string}`[] = [
      'route:vwb-demo',
      'route:application',
      'controller:vwb-demo',
      'controller:application',
    ];
    containersToReplace.forEach((containerName) => {
      let instance = appInstance.lookup(containerName) as any;
      if (instance == null) {
        return;
      }

      if (instance[serviceName] == null) {
        return;
      }

      // We use defineProperty rather than direct assignment or .set, because
      // technically service properties are readonly properties and can't
      // normally be written to
      Object.defineProperty(instance, serviceName, {
        value: serviceInstance,
        configurable: true,
      });
    });
  }

  private overrideAdapters() {
    let appInstance = getOwner(this);

    // Override all registered adapters to not send API calls. This uses
    // internal Ember APIs and is subject to change.
    // It is not enough to simply overwrite the ApplicationAdapter, because once
    // the app boots the adapters that extend from it have already been
    // instantiated - so we need to loop through all the existing ones and
    // replace them.
    let registeredAdapters: Record<`${string}:${string}`, boolean> =
      (appInstance as any | null)?.__registry__?.knownForType?.('adapter') ?? {};
    Object.keys(registeredAdapters).forEach((name: `${string}:${string}`) => {
      let adapter = appInstance.lookup(name);

      if (adapter == null) {
        return;
      }

      let factory = appInstance.factoryFor(name);

      if (factory?.class == null) {
        return;
      }

      class AdapterMockClass extends (factory.class as any) {
        findRecord(store: any, type: any, id: any, snapshot: any) {
          if (!config.isProduction) {
            /* eslint-disable no-console */
            console.log('findRecord called:');
            console.log({ store, type, id, snapshot });
            /* eslint-enable no-console */
          }

          if (snapshot?.modelName != null) {
            return resolve({
              [underscore(singularize(snapshot.modelName))]: null,
            });
          }
          return resolve({});
        }
        findAll(store: any, type: any, neverSet: any, snapshotRecordArray: any) {
          if (!config.isProduction) {
            /* eslint-disable no-console */
            console.log('findAll called:');
            console.log({ store, type, neverSet, snapshotRecordArray });

            /* eslint-enable no-console */
          }

          if (snapshotRecordArray?.modelName != null) {
            return resolve({
              [underscore(pluralize(snapshotRecordArray.modelName))]: [],
            });
          }
          return resolve([]);
        }
        query(store: any, type: any, query: any, recordArray: any, adapterOptions: any) {
          if (!config.isProduction) {
            /* eslint-disable no-console */
            console.log('query called:');
            console.log({ store, type, query, recordArray, adapterOptions });
            /* eslint-enable no-console */
          }

          if (recordArray?.modelName != null) {
            return resolve({
              [underscore(pluralize(recordArray.modelName))]: [],
            });
          }
          return resolve([]);
        }
        queryRecord(store: any, type: any, query: any, adapterOptions: any) {
          if (!config.isProduction) {
            /* eslint-disable no-console */
            console.log('queryRecord called:');
            console.log({ store, type, query, adapterOptions });
            /* eslint-enable no-console */
          }

          if (type?.modelName != null) {
            return resolve({
              [underscore(singularize(type.modelName))]: null,
            });
          }
          return resolve({});
        }
        ajax(url: string, type: string, options: any) {
          if (!config.isProduction) {
            /* eslint-disable no-console */
            console.log('Store request called:');
            console.log(url, type, options);
            /* eslint-enable no-console */
          }

          return resolve({});
        }
      }

      this.replaceRegistration(name, AdapterMockClass);
    });

    Object.entries(CUSTOM_ADAPTER_REPLACEMENTS).forEach(([name, klass]: [`${string}:${string}`, Class]) =>
      this.replaceRegistration(name, klass),
    );
  }

  private replaceRegistration(name: `${string}:${string}`, klass: Class) {
    let appInstance = getOwner(this);
    // @ts-ignore - untyped
    appInstance.unregister(name);
    appInstance.register(name, klass);
  }

  private attachGlobalClickListener() {
    document.addEventListener('click', this.onAnyClick, { capture: true });
  }

  private detachGlobalClickListener() {
    document.removeEventListener('click', this.onAnyClick, { capture: true });
  }

  @action
  onAnyClick(event: FlyMouseEvent<HTMLElement>) {
    if (!event.isTrusted) {
      // If the event was generated by a script, don't report it to the parent.
      // We only want "real" click events to be reported
      return;
    }

    // Notifies parent page (marketing pages) of all click events, so that we
    // can show popups encouraging leads to sign up after certain actions
    let target: null | Record<string, any> = null;

    if (event.target != null) {
      let attributes: Record<string, string> = {};
      if (event.target.hasAttributes()) {
        let attrs = event.target.attributes;
        for (let i = 0; i < attrs.length; i++) {
          if (attrs[i].name != null) {
            attributes[attrs[i].name] = attrs[i].value;
          }
        }
      }

      target = {
        tagName: event.target.tagName,
        innerText: event.target.innerText,
        attributes,
      };
    }

    this.windowMessenger.send('click', { target: target != null ? { ...target } : null });
  }
}
